import React from 'react';
import { CreditCard, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

interface DualCreditDisplayProps {
  analysisCredits: number;
  adCreativeCredits: number;
  showLabels?: boolean;
  size?: 'small' | 'medium' | 'large';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export function DualCreditDisplay({
  analysisCredits,
  adCreativeCredits,
  showLabels = true,
  size = 'medium',
  orientation = 'horizontal',
  className = ''
}: DualCreditDisplayProps) {
  const sizeClasses = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base'
  };

  const iconSizes = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-5 h-5'
  };

  const containerClasses = orientation === 'horizontal' 
    ? 'flex items-center gap-4' 
    : 'flex flex-col gap-2';

  return (
    <div className={`${containerClasses} ${className}`}>
      {/* Analysis Credits */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-2"
      >
        <div className="flex items-center gap-1.5 bg-gradient-to-r from-purple-50 to-blue-50 px-3 py-1.5 rounded-lg border border-purple-200">
          <CreditCard className={`${iconSizes[size]} text-purple-600`} />
          <span className={`${sizeClasses[size]} font-semibold text-purple-700`}>
            {analysisCredits}
          </span>
          {showLabels && (
            <span className={`${sizeClasses[size]} text-purple-600`}>
              {size === 'small' ? 'analysis' : 'analysis credits'}
            </span>
          )}
        </div>
      </motion.div>

      {/* Ad Creative Credits */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-2"
      >
        <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-50 to-orange-50 px-3 py-1.5 rounded-lg border border-amber-200">
          <Sparkles className={`${iconSizes[size]} text-amber-600`} />
          <span className={`${sizeClasses[size]} font-semibold text-amber-700`}>
            {adCreativeCredits}
          </span>
          {showLabels && (
            <span className={`${sizeClasses[size]} text-amber-600`}>
              {size === 'small' ? 'creative' : 'creative credits'}
            </span>
          )}
        </div>
      </motion.div>
    </div>
  );
}